import { useLayoutEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainContainer from "../../components/container/MainContainer";
import PageTitle from "../../components/titles/PageTitle";
import Loading from "../../components/loading/Loading";
import TextContainer from "../../components/text/TextContainer";
import Modal from "../../components/modal/Modal";
import { openModal } from "../../features/modalSLice";
import { useAuthorization } from "../../hooks/useAuthorization";
import AddDonor from "./AddDonor";
import DeleteDonor from "./DeleteDonor";
import { getDonor, unsetSelectedDonor } from "../../features/donorsSlice";
import EditReformTemplate from "./EditReformTemplate";
import { ChevronLeft, FilePen, Trash2 } from "lucide-react";
import loadStatus from "../../util/loadStatus";
import NetworkError from "../error/NetworkError";
import SecondContainer from "../../components/container/SecondContainer";

const SingleDonor = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthorized } = useAuthorization();
  const modalContent = useSelector((state) => state.modal.content);
  const donor = useSelector((state) => state.donors.selected);
  const reformulations = useSelector((state) => state.donors.reformulations);
  const loading = useSelector((state) => state.donors.load.getDonor);
  const reformulationItems = useSelector(
    (state) => state.config.reformulationItems
  );

  const openEditDonorModal = (edit, id) => {
    dispatch(openModal(<AddDonor edit={edit} id={id} />));
  };

  const openDeleteDonorModal = (id) => {
    dispatch(openModal(<DeleteDonor id={id} />));
  };

  const openEditReformulationsModal = (id) => {
    dispatch(
      openModal(
        <EditReformTemplate id={id} reformulations={reformulations || []} />
      )
    );
  };

  useLayoutEffect(() => {
    dispatch(getDonor(id));
    return () => {
      dispatch(unsetSelectedDonor());
    };
  }, []);

  if (loading === loadStatus.loading) {
    return <Loading />;
  }

  if (loading === loadStatus.error) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <SecondContainer>
      <Modal>{modalContent}</Modal>
      {donor.id && (
        <>
          <PageTitle title={`${donor.name}`} />
          <div className="bg-content  border border-bkg2 rounded-md p-7 mb-5 !text-text-1/55">
            <TextContainer label="Name" content={`${donor.name}`} />

            <TextContainer label="Address 1" content={donor.address1} />
            <TextContainer label="Address 2" content={donor.address2} />

            <TextContainer label="Email" content={donor.email || "N/A"} />
            <TextContainer label="Mobile" content={donor.phone || "N/A"} />
            <TextContainer label="Phone" content={donor.phone2 || "N/A"} />
            <TextContainer label="Province" content={donor.name || "N/A"} />
            <TextContainer label="Country" content={donor.country || "N/A"} />
          </div>
          <div className="p-4 border border-bkg2 rounded-md  mb-5 text-text-1/80">
            <h5>Reformulation Template</h5>
            <div className="p-5">
              <ol>
                {reformulations.length <= 0 && (
                  <li>
                    This Donor will use the system template unless changed
                  </li>
                )}
                <li>Follow this instructions carefully</li>
                <li>Please arrage the reformulations from top to bottom</li>
                <li>
                  Select the relevant item for the donor and add the required
                  rename.
                </li>
              </ol>
            </div>

            <div className="flex gap-3">
              <button
                onClick={(e) => openEditReformulationsModal(id)}
                disabled={!isAuthorized("donors", "update")}
                className="add-rss-btn"
              >
                <FilePen size={18} />
                Edit Template
              </button>
            </div>
            <div className="table-container !w-full mt-5 text-nowrap">
              <table>
                <thead>
                  <tr>
                    <td className="p-2 py-2 bg-gray-600 text-white">No</td>
                    <td className="p-2 py-2 bg-gray-600 text-white">Name</td>
                    <td className="p-2 py-2 bg-gray-600 text-white !text-left">
                      Rename
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {reformulations.map((item, index) => (
                    <tr key={index}>
                      <td className="p-2">{item.number}</td>
                      <td className="p-2">
                        {reformulationItems.find((i) => i.value === item.name)
                          ? reformulationItems.find(
                              (i) => i.value === item.name
                            ).name
                          : item.name}
                      </td>
                      <td className="p-2 !text-left">{item.rename}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-7 border border-bkg2 rounded-md flex justify-between items-center">
            <Link to="/settings/donors" className="cancel-rss-btn">
              <ChevronLeft size={18} /> Back
            </Link>
            <div className="flex gap-3">
              <button
                onClick={(e) => openEditDonorModal(true, id)}
                disabled={!isAuthorized("donors", "update")}
                className="add-rss-btn"
              >
                <FilePen size={18} />
                Edit
              </button>
              <button
                onClick={(e) => openDeleteDonorModal(id)}
                disabled={!isAuthorized("donors", "delete")}
                className="delete-rss-btn"
              >
                <Trash2 size={18} />
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </SecondContainer>
  );
};

export default SingleDonor;
